import { createDraftSafeSelector, createEntityAdapter, createSlice, Draft, PayloadAction, Reducer } from "@reduxjs/toolkit";
import { AsyncEntityState, createAsyncEntityInitialState } from "../../../Store/Thunks/asyncEntityStateUtil";
import { fetchSectionsBySystemIdsThunk } from "../../../Store/Thunks/sectionsBySystemIds";
import { RootState, useAppSelector } from "../../../Store";
import { Location } from "../LocationFeedingCalls/GQLCalls/getLfStructureCall";
import { isEqual } from "lodash";

export type LocationWithSectionAndSystem = Location & { systemId: number, sectionId: number }

const selectId = (location: LocationWithSectionAndSystem) => location.location_id;

const entityAdapter = createEntityAdapter({ selectId });
const { updateMany, updateOne, upsertOne, upsertMany, addOne, addMany, removeAll, removeOne } = entityAdapter;

const initialState = createAsyncEntityInitialState(entityAdapter.getInitialState());

const { actions, reducer, getSelectors } = createSlice({
  name: "locations",
  initialState,
  reducers: {
    // default actions -- can be overwritten in the supplied reducers if needed.
    updateMany,
    updateOne,
    upsertOne,
    upsertMany,
    addOne,
    addMany,
    removeAll,
    removeOne,
    // default action
    clearPendingIds: (state: Draft<AsyncEntityState<LocationWithSectionAndSystem, number>>) => { state.enqueueIds = [] },
    addPendingId: (state: Draft<AsyncEntityState<LocationWithSectionAndSystem, number>>, action: PayloadAction<Draft<number>>) => {
      state.enqueueIds.push(action.payload)
    },
    addLoadingIds: (state: Draft<AsyncEntityState<LocationWithSectionAndSystem, number>>, action: PayloadAction<Draft<number[]>>) => {
      state.loadingIds.push(...action.payload)
    },
  },
  extraReducers(builder) {
    builder.addCase(fetchSectionsBySystemIdsThunk.fulfilled, (state, action) => {
      entityAdapter.upsertMany(state, action.payload.flatMap((section) => (section.section_locations.map((location) => ({ ...location, systemId: section.system_id, sectionId: section.section_id })))))
    })
  },
  selectors: {
    selectLocationsBySystem: createDraftSafeSelector(
      entityAdapter.getSelectors().selectAll,
      (_, externalIds: number[]) => externalIds,
      (entities: LocationWithSectionAndSystem[], externalIds: number[]) =>
        entities.filter(entity => entity.systemId && externalIds.includes(entity.systemId))
    ),
    selectLocationsBySection: createDraftSafeSelector(
      entityAdapter.getSelectors().selectAll,
      (_, externalIds: number[]) => externalIds,
      (entities: LocationWithSectionAndSystem[], externalIds: number[]) =>
        entities.filter(entity => entity.sectionId && externalIds.includes(entity.sectionId))
    ),
  }
})

// export const useLocationById = (id?: number) =>
//   useAppSelector((state) =>
//     id !== undefined ? selectLocationById(state, id) : undefined
//   );

export const useLocationById = (id?: number) => {
  return useAppSelector((state) => {
    const entity = id !== undefined ? selectLocationById(state, id) : undefined;

    const isLoading = id
      ? state.graphQlData.locations.loadingIds.includes(id)
      : false;

    return { entity, isLoading };
  }, isEqual);
};

export const locationReducer = reducer as Reducer<AsyncEntityState<LocationWithSectionAndSystem, number>>;

const stateSelector = (state: RootState) => state.graphQlData.locations;

export const { removeAll: clearLocations, removeOne: removeLocation, upsertMany: upsertLocations, updateMany: updateLocations, updateOne: updateSingleLocation } = actions;
export const { selectLocationsBySystem, selectLocationsBySection, } = getSelectors(stateSelector)
export const { selectById: selectLocationById, selectAll: selectAllLocations } = entityAdapter.getSelectors(stateSelector)


export function maxRowsAndCols(acc: { rows: number, cols: number }, objWithVisual: { visual?: string }) {
  if (!objWithVisual.visual) return acc;

  let rows = 0;
  let cols = 0;

  if (objWithVisual.visual.includes("-")) {
    const afterDash = objWithVisual.visual.split("-")[1];
    [rows, cols] = afterDash.split("/").map(Number);
  } else {
    const [endRow, endCol] = objWithVisual.visual.split('/').map(Number);
    return {
      rows: Math.max(acc.rows, endRow),
      cols: Math.max(acc.cols, endCol),
    };
  }

  return {
    rows: Math.max(acc.rows, rows),
    cols: Math.max(acc.cols, cols),
  };
}


export const selectLocationMaxGridSpans = createDraftSafeSelector(
  [selectLocationsBySection, (_, locationIds: number[]) => locationIds],
  (locationForSection, _locationIds) => {
    if (!locationForSection.length) {
      return { rows: 0, cols: 0 }
    }

    return locationForSection.reduce(
      maxRowsAndCols,
      { rows: 0, cols: 0 })
  }
);
